// vanilla/components/gallery/FullImageViewer.tsx

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import "./full-image-viewer.scss";

interface ImageProps {
  img: {
    id: string;
    src_thumb: string;
    src_full: string;
    alt: string;
  } | null;
  onClose: () => void;
}

const FullImageViewer: React.FC<ImageProps> = ({ img, onClose }) => {
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const memoX = useRef(0);
  const memoY = useRef(0);

  const highResUrl = useMemo(() => {
    if (!img?.src_full) return "";
    return img.src_full
      .replace("w=512", "w=1920")
      .replace("fit=crop", "fit=max");
  }, [img?.src_full]);

  // Bloqueia o scroll da página por trás (body)
  useEffect(() => {
    if (!img) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [img]);

  // Fecha o fullscreen ao apertar ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Limpa o Pan se a escala voltar a ser original
  useEffect(() => {
    if (scale <= 1) {
      setTranslateX(0);
      setTranslateY(0);
      memoX.current = 0;
      memoY.current = 0;
    }
  }, [scale]);

  const handleMouseDownPan = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (scale <= 1) return;
      e.preventDefault();

      isDragging.current = true;
      startX.current = e.clientX;
      startY.current = e.clientY;
      (window as any).lastDeltaX = 0;
      (window as any).lastDeltaY = 0;

      const moveHandler = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;
        const deltaX = moveEvent.clientX - startX.current;
        const deltaY = moveEvent.clientY - startY.current;

        (window as any).lastDeltaX = deltaX;
        (window as any).lastDeltaY = deltaY;

        setTranslateX(memoX.current + deltaX);
        setTranslateY(memoY.current + deltaY);
      };

      const upHandler = () => {
        isDragging.current = false;
        memoX.current += (window as any).lastDeltaX || 0;
        memoY.current += (window as any).lastDeltaY || 0;

        window.removeEventListener("mousemove", moveHandler);
        window.removeEventListener("mouseup", upHandler);
      };

      window.addEventListener("mousemove", moveHandler);
      window.addEventListener("mouseup", upHandler);
    },
    [scale],
  );

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const ZOOM_SPEED = 0.15;
    const MIN_SCALE = 0.8;
    const MAX_SCALE = 4;

    setScale((prevScale) =>
      Math.min(
        MAX_SCALE,
        Math.max(
          MIN_SCALE,
          prevScale + (e.deltaY > 0 ? -ZOOM_SPEED : ZOOM_SPEED),
        ),
      ),
    );
  }, []);

  if (!img) return null;

  const viewerStyle: React.CSSProperties = {
    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
    cursor: scale === 1 ? "default" : "grab",
    transition: isDragging.current ? "none" : "transform 0.1s ease-out",
  };

  // Renderiza todo o conteúdo do modal acoplado diretamente na raiz do body do HTML
  return createPortal(
    <div className="photo-fullscreen-overlay" onClick={onClose}>
      <button
        className="fullscreen-close-btn"
        onClick={onClose}
        aria-label="Fechar"
      >
        &times;
      </button>

      <div
        className="full-image-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="full-image-viewport"
          onWheel={handleWheel}
          onMouseDown={handleMouseDownPan}
          role="img"
          aria-label={img.alt}
        >
          <img
            src={highResUrl}
            alt={img.alt}
            className="full-image-element"
            style={viewerStyle}
            draggable={false}
          />
        </div>

        <div className="viewer-controls">
          <button
            className="control-button"
            onClick={() => setScale((prev) => Math.min(4, prev + 0.4))}
            aria-label="Zoom In"
          >
            +
          </button>
          <button
            className="control-button"
            onClick={() => setScale((prev) => Math.max(0.8, prev - 0.4))}
            aria-label="Zoom Out"
          >
            -
          </button>
        </div>
      </div>
    </div>,
    document.body, // Injeta o nó DOM direto no body
  );
};

export default FullImageViewer;
